import { canLoadTracking } from '@/utils/tracking.utils';
import type { AnalyticsEventName } from './events';
import {
  hasCookieConsent,
  readCookieConsentFromDocument,
  type CookieConsentCategory,
  type ICookieConsent,
} from '@/utils/cookieConsent.utils';

export type AnalyticsEventPayload = Record<string, unknown>;
export type AnalyticsUserProperties = Record<string, string | number | boolean | null>;

/** Identifies the signed-in user to ad platforms for matching; GA4 must never receive the email. */
export interface IAnalyticsUserData {
  id: string;
  email: string;
}

export interface Analytics {
  setUserId(userId: string | null): void;
  setUserData(userData: IAnalyticsUserData | null): void;
  setUserProperties(properties: AnalyticsUserProperties): void;
  trackPageview(path?: string, payload?: AnalyticsEventPayload): void;
  trackEvent(name: AnalyticsEventName, payload?: AnalyticsEventPayload, properties?: AnalyticsUserProperties): void;
}

/** Providers implement vendor APIs; consent, readiness and event delivery belong to the manager. */
export interface AnalyticsProvider {
  readonly name: string;
  readonly consentCategories: readonly CookieConsentCategory[];
  init?(): void | Promise<void>;
  dispose?(): void | Promise<void>;
  setConsent?(consent: ICookieConsent | null): void;
  setUserId?(userId: string | null): void;
  setUserData?(userData: IAnalyticsUserData | null): void;
  setUserProperties?(properties: AnalyticsUserProperties): void;
  trackPageview?(
    path?: string,
    payload?: AnalyticsEventPayload,
    properties?: AnalyticsUserProperties,
  ): void | Promise<void>;
  trackEvent?(
    name: AnalyticsEventName,
    payload?: AnalyticsEventPayload,
    properties?: AnalyticsUserProperties,
  ): void | Promise<void>;
}

type ProviderCall = () => void | Promise<void>;

// Bound memory use if an opted-in SDK never finishes loading.
const MAX_PENDING_EVENTS = 200;

interface IProviderState {
  provider: AnalyticsProvider;
  status: 'idle' | 'loading' | 'ready' | 'failed';
  pending: ProviderCall[];
  pageviewSent: boolean;
}

export class AnalyticsManager implements Analytics {
  private readonly providers = new Map<string, IProviderState>();
  private userId: string | null | undefined;
  private userData: IAnalyticsUserData | null | undefined;
  private userProperties: AnalyticsUserProperties | undefined;
  private currentPage: (() => string | undefined) | undefined;

  constructor(
    private readonly readConsent: () => ICookieConsent | null = readCookieConsentFromDocument,
    // Null until the environment decides; opted-in events wait in the provider queues meanwhile.
    private trackingEnabled: boolean | null = true,
  ) {}

  /** Only environments that enable tracking load any tracker, so QA and dev never feed production data. */
  public setTrackingEnabled(enabled: boolean): void {
    this.trackingEnabled = enabled;
    this.syncConsent();
  }

  public registerProvider(provider: AnalyticsProvider): void {
    this.unregisterProvider(provider.name);
    const state: IProviderState = {
      provider,
      status: 'idle',
      pending: [],
      pageviewSent: false,
    };
    this.providers.set(provider.name, state);
    this.initialize(state);
  }

  public unregisterProvider(name: string): void {
    const state = this.providers.get(name);
    if (!state) return;
    this.providers.delete(name);
    state.pending = [];
    if (state.status === 'ready' || state.status === 'loading') {
      try {
        void Promise.resolve(state.provider.dispose?.()).catch((error: unknown) => this.reportError(state, error));
      } catch (error) {
        this.reportError(state, error);
      }
    }
  }

  public setCurrentPage(getPage: () => string | undefined): void {
    this.currentPage = getPage;
  }

  public syncConsent(): void {
    const consent = this.readConsent();
    for (const state of this.providers.values()) {
      if (state.status === 'ready' || state.status === 'loading') {
        try {
          state.provider.setConsent?.(consent);
        } catch (error) {
          this.reportError(state, error);
        }
      }
      if (!this.canTrack(state)) state.pending = [];
      this.initialize(state);
      const path = this.currentPage?.();
      if (!state.pageviewSent && path && canLoadTracking()) this.deliverPageview(state, path);
    }
  }

  public trackPageview(path?: string, payload?: AnalyticsEventPayload): void {
    if (!canLoadTracking()) return;
    for (const state of this.providers.values()) this.deliverPageview(state, path, payload);
  }

  private deliverPageview(state: IProviderState, path?: string, payload?: AnalyticsEventPayload): void {
    if (!this.canTrack(state)) return;
    state.pageviewSent = true;
    const properties = this.snapshotUserProperties();
    this.deliver(state, () => state.provider.trackPageview?.(path, payload, properties));
  }

  public trackEvent(
    name: AnalyticsEventName,
    payload?: AnalyticsEventPayload,
    properties?: AnalyticsUserProperties,
  ): void {
    const eventProperties = this.snapshotUserProperties(properties);
    for (const state of this.providers.values()) {
      this.deliver(state, () => state.provider.trackEvent?.(name, payload, eventProperties));
    }
  }

  private snapshotUserProperties(properties?: AnalyticsUserProperties): AnalyticsUserProperties {
    // Queued events retain their context even if the current tenant changes before delivery.
    return Object.freeze({ ...this.userProperties, ...properties });
  }

  public setUserId(userId: string | null): void {
    this.userId = userId;
    for (const state of this.providers.values()) {
      if (state.status === 'ready') this.deliver(state, () => state.provider.setUserId?.(userId));
      else this.initialize(state);
    }
  }

  public setUserData(userData: IAnalyticsUserData | null): void {
    this.userData = userData;
    for (const state of this.providers.values()) {
      if (state.status === 'ready') this.deliver(state, () => state.provider.setUserData?.(userData));
      else this.initialize(state);
    }
  }

  public setUserProperties(properties: AnalyticsUserProperties): void {
    this.userProperties = { ...properties };
    for (const state of this.providers.values()) {
      if (state.status === 'ready') this.deliver(state, () => state.provider.setUserProperties?.(this.userProperties!));
      else this.initialize(state);
    }
  }

  private canTrack(state: IProviderState): boolean {
    return (
      this.trackingEnabled !== false &&
      this.providers.get(state.provider.name) === state &&
      hasCookieConsent(this.readConsent(), state.provider.consentCategories)
    );
  }

  private initialize(state: IProviderState): void {
    if (state.status !== 'idle' || this.trackingEnabled !== true || !this.canTrack(state) || !canLoadTracking()) return;
    state.status = 'loading';
    try {
      state.provider.setConsent?.(this.readConsent());
      const result = state.provider.init?.();
      if (result) {
        void result
          .then(() => this.finishInitialization(state))
          .catch((error: unknown) => this.failInitialization(state, error));
      } else {
        this.finishInitialization(state);
      }
    } catch (error) {
      this.failInitialization(state, error);
    }
  }

  private finishInitialization(state: IProviderState): void {
    state.status = 'ready';
    this.execute(state, () => {
      if (this.userId !== undefined) state.provider.setUserId?.(this.userId);
      if (this.userData !== undefined) state.provider.setUserData?.(this.userData);
      if (this.userProperties !== undefined) state.provider.setUserProperties?.(this.userProperties);
    });
    const pending = state.pending.splice(0);
    for (const call of pending) this.execute(state, call);
  }

  private failInitialization(state: IProviderState, error: unknown): void {
    state.status = 'failed';
    state.pending = [];
    this.reportError(state, error);
  }

  private deliver(state: IProviderState, call: ProviderCall): void {
    // Never collect pre-consent events. Only opted-in events may wait for an SDK to finish loading.
    if (!this.canTrack(state)) return;
    this.initialize(state);
    if (state.status === 'ready') this.execute(state, call);
    else if (state.status !== 'failed') {
      if (state.pending.length >= MAX_PENDING_EVENTS) state.pending.shift();
      state.pending.push(call);
    }
  }

  private execute(state: IProviderState, call: ProviderCall): void {
    // Recheck at delivery: consent may expire or change while an SDK is loading.
    if (!this.canTrack(state) || !canLoadTracking()) return;
    try {
      void Promise.resolve(call()).catch((error: unknown) => this.reportError(state, error));
    } catch (error) {
      this.reportError(state, error);
    }
  }

  private reportError(state: IProviderState, error: unknown): void {
    console.error(`[analytics] provider failed (${state.provider.name})`, error);
  }
}

const analyticsManager = new AnalyticsManager(readCookieConsentFromDocument, null);

export function registerAnalyticsProviders(providers: AnalyticsProvider[]): void {
  for (const provider of providers) analyticsManager.registerProvider(provider);
}

export function setAnalyticsCurrentPage(getPage: () => string | undefined): void {
  analyticsManager.setCurrentPage(getPage);
}

export function setAnalyticsTrackingEnabled(enabled: boolean): void {
  analyticsManager.setTrackingEnabled(enabled);
}

export function syncAnalyticsConsent(): void {
  analyticsManager.syncConsent();
}

export function getAnalytics(): Analytics {
  return analyticsManager;
}
